/**
 * Application shell — Document 6 §8.1.
 *
 * The active instructor chip is always visible and always one click from
 * changing. On a shared studio tablet the user must never have to guess who
 * the system is currently attributing work to.
 *
 * There is no sidebar: the product has three primary destinations, and a
 * sidebar would be scaffolding without content.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { useSession } from '../session/SessionContext.js';
import { InstallAppDialog } from './InstallAppDialog.js';
import './AppShell.css';

/** True once "Ana Ekrana Ekle" has already been done — the menu item that
 *  explains how is pointless once there is nothing left to explain. */
function isInstalled(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari's own flag; not covered by the media query above.
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

const NAV_ITEMS = [
  { to: '/panel', label: 'Panel', ownerOnly: false },
  { to: '/ogrenciler', label: 'Öğrenciler', ownerOnly: false },
  { to: '/raporlar', label: 'Raporlar', ownerOnly: false },
  // Creating an account is creating a login; it belongs to whoever holds the
  // studio password. Hiding the link is a courtesy — the API is the boundary.
  { to: '/egitmenler', label: 'Eğitmenler', ownerOnly: true },
];

export function AppShell() {
  const { status, session, refresh, signOut } = useSession();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** Back to the picker, with nothing else reachable until someone signs in. */
  async function leaveProfile() {
    setMenuOpen(false);
    await api.post('/api/auth/leave-instructor');
    await refresh();
    navigate('/egitmen-secimi', { replace: true });
  }

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (status === 'loading') {
    return <div className="shell shell--loading" aria-busy="true" />;
  }
  if (status === 'anonymous') return <Navigate to="/giris" replace />;

  // A password the studio owner chose and read out is not a secret; nothing
  // else opens until it has been replaced.

  /*
   * Work is attributed to a person, so an instructor session always has one —
   * their own account. The owner is asked, but not held: a studio with no
   * instructors yet would otherwise bounce its owner back to a picker with
   * nothing in it, unable to reach the screen that adds one.
   */
  if (!session) return <Navigate to="/giris" replace />;
  /*
   * Nobody is working in this session: no profile in use, and no manager grant
   * bought with the studio password. It belongs at the picker, which is also
   * the only thing the API will answer for it.
   */
  if (!session.activeInstructor && !session.managerMode) {
    return <Navigate to="/egitmen-secimi" replace />;
  }

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__brand">
          <span className="shell__brand-name">{session.studio.name}</span>
        </div>

        <nav className="shell__nav" aria-label="Ana gezinme">
          {NAV_ITEMS.filter((item) => !item.ownerOnly || session.role === 'owner').map(
            (item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? 'shell__nav-link shell__nav-link--active' : 'shell__nav-link'
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="shell__actions" ref={menuRef}>
          <button
            type="button"
            className="shell__instructor"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <span className="shell__instructor-avatar" aria-hidden="true">
              {(session.activeInstructor?.name ?? 'Yönetici')
                .charAt(0)
                .toLocaleUpperCase('tr')}
            </span>
            {session.activeInstructor?.name ?? 'Yönetici'}
            <span aria-hidden="true">▾</span>
          </button>

          {menuOpen && (
            <div className="shell__menu" role="menu">
              {/*
               * Leaving a profile drops the session back to the studio it
               * signed in to. An instructor who signed in as themselves has
               * no such session behind them, so for them the way out is
               * signing out, which the last item already offers.
               */}
              {session.role === 'owner' && (
                <button
                  type="button"
                  role="menuitem"
                  className="shell__menu-item"
                  onClick={() => void leaveProfile()}
                >
                  {session.activeInstructor ? 'Profilden Çık' : 'Eğitmen Seç'}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="shell__menu-item"
                onClick={() => navigate('/ayarlar')}
              >
                Stüdyo Ayarları
              </button>
              {!isInstalled() && (
                <button
                  type="button"
                  role="menuitem"
                  className="shell__menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    setInstallOpen(true);
                  }}
                >
                  Uygulamayı Yükle
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="shell__menu-item shell__menu-item--danger"
                onClick={() => void signOut()}
              >
                Çıkış Yap
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="shell__main">
        <Outlet />
      </main>

      {installOpen && <InstallAppDialog onClose={() => setInstallOpen(false)} />}
    </div>
  );
}
